// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip, { TooltipPresentationProps } from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import { xParserOptions } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import React, { ChangeEvent, useEffect, useState } from "react";

interface Props {
    label: string;
    tooltipText?: string;
    value: number | undefined;
    currentUnit?: string;
    units: { [key: string]: string };
    message?: { message: string; type: string };
    onChange: (value: number) => void;
    labelIsHorizontal?: boolean;
    extendedStyles?: JettingAppTextFieldStyles;
    maxValue?: number;
    minValue?: number;
    updateFieldImmediately?: boolean;
    readOnly?: boolean;
}

export interface JettingAppTextFieldStyles {
    wrapper?: InjectableCss;
    textField?: TextFieldProps;
    tooltip?: TooltipPresentationProps;
    unit?: InjectableCss;
}

export const jettingAppTextFieldStyles: JettingAppTextFieldStyles = {};

const JettingAppTextField = ({
    extendedStyles,
    label,
    value,
    tooltipText,
    onChange,
    labelIsHorizontal = true,
    currentUnit,
    message,
    maxValue,
    minValue = 0,
    updateFieldImmediately = false,
    readOnly,
}: Props) => {
    const [styles] = useState(() => mergeToNew(jettingAppTextFieldStyles, extendedStyles));
    const [inputValue, setInputValue] = useState(value?.toString());
    const [searchTimeoutId, setSearchTimeoutId] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (value === Number(inputValue)) {
            return;
        }
        setInputValue(`${value}`);
    }, [value]);

    const textLabel = tooltipText ? (
        <>
            {label}
            <Tooltip {...styles.tooltip} passAsChildren={true}>
                <Typography>{parse(tooltipText, xParserOptions)}</Typography>
            </Tooltip>
        </>
    ) : (
        label
    );

    /**
     * Handles the input change event for a text field, updating the input value and triggering
     * a debounced callback to validate and process the input as a number within a specified range.
     *
     * @param event - The change event triggered by the input field.
     *
     * The function performs the following:
     * - Updates the input value state immediately.
     * - Clears any existing timeout for the debounced callback.
     * - Sets a new timeout to validate and process the input after 800ms.
     * - If the input can be parsed as a number:
     *   - Ensures the number is within the specified range (`minValue` to `maxValue`).
     *   - Updates the input field value and state with the validated number.
     *   - Calls the `onChange` callback with the validated number.
     */
    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setInputValue(value);
        if (updateFieldImmediately) {
            onChange(Number(value));
            return;
        }

        if (typeof searchTimeoutId === "number") {
            clearTimeout(searchTimeoutId);
        }
        setSearchTimeoutId(
            setTimeout(() => {
                setInputValue(value);

                const possibleNumber = Number(value);
                if (!Number.isNaN(possibleNumber)) {
                    let newValue = possibleNumber;
                    if (possibleNumber < minValue) {
                        newValue = minValue;
                    } else if (possibleNumber > (maxValue ?? Number.POSITIVE_INFINITY)) {
                        newValue = maxValue ?? Number.POSITIVE_INFINITY;
                    }
                    event.target.value = newValue.toString();
                    setInputValue(newValue.toString());
                    onChange(newValue);
                }
            }, 800),
        );
    };

    return (
        <StyledWrapper {...styles.wrapper} {...{ labelIsHorizontal, hasUnit: !!currentUnit }}>
            <TextField
                translate="no"
                placeholder={`${translate("Add")} ${label}`}
                label={textLabel}
                onChange={onInputChange}
                value={inputValue}
                required
                requiredLabel=" "
                type="number"
                min={0}
                data-test-selector="jetting-field"
                error={message?.message}
                {...styles.textField}
                disabled={readOnly}
            />
            {currentUnit && (
                <StyledWrapper {...styles.unit} translate="no">
                    {currentUnit}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
export default JettingAppTextField;
